import React, { PureComponent } from 'react';
import { ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { identity, isEmpty } from 'ramda';

import {
  Container,
  Details,
  Name,
  Intro,
  WeProvide,
  Summary,
  Description,
  Gallery,
  Content,
  CoverContentWrapper,
  Half,
  GalleryWrapper,
  MarketLink,
} from './experienceInfo.styles';
import messages from './experienceInfo.messages';
import { Button } from '../../../shared/components/button';
import { Divider } from '../../../shared/components/divider';
import { Section } from '../../../shared/components/section';
import { HiddenOnPlatform } from '../../../shared/components/hiddenOnPlatform';
import { PADDING_BIG, PADDING_SMALL } from '../../../shared/components/divider/divider.constants';
import { renderWhenNotNil, renderWhenNotNilOrEmpty } from '../../../shared/utils/rendering';
import { BookingStep } from '../../booking/booking.constants';
import { ROUTES } from '../../routes';

export class ExperienceInfo extends PureComponent {
  static propTypes = {
    experience: PropTypes.object.isRequired,
    contextName: PropTypes.string,
    match: PropTypes.object,
    intl: PropTypes.object,
    currentExperienceParentRoute: PropTypes.string,
    onDiscountInfoClick: PropTypes.func,
  };

  static defaultProps = {
    onDiscountInfoClick: identity,
  };

  get videoGalleryItems() {
    const { heroVideo, heroVideoPlaceholder } = this.props.experience;
    if (!heroVideo) {
      return [];
    }

    return [
      {
        imageAsset: heroVideoPlaceholder,
        videoAsset: heroVideo,
      },
    ];
  }

  get galleryItems() {
    if (this.isLoadingExperience) {
      return [];
    }
    const { experience } = this.props;
    return [...this.videoGalleryItems, ...experience.heroGallery.map(imageAsset => ({ imageAsset }))];
  }

  get bookingUrl() {
    const basePath = this.props.match.url.replace(/\/$/, '');
    return basePath + ROUTES.booking[BookingStep.TIME];
  }

  get isLoadingExperience() {
    return isEmpty(this.props.experience);
  }

  handleDiscountInfoClick = () => this.props.onDiscountInfoClick();

  renderWeProvideSection = () => {
    const weProvide = this.props.experience.weProvide;

    return renderWhenNotNil(() => (
      <>
        <Divider paddingSize={PADDING_BIG} />
        <FormattedMessage {...messages.weProvideTitle}>
          {weProvideTitle => (
            <Section title={weProvideTitle}>
              <WeProvide text={weProvide} />
            </Section>
          )}
        </FormattedMessage>
      </>
    ))(weProvide);
  };

  renderContent = renderWhenNotNilOrEmpty(() => {
    const { intl, currentExperienceParentRoute } = this.props;
    const { name, intro, description } = this.props.experience;

    return (
      <>
        <CoverContentWrapper>
          <MarketLink to={currentExperienceParentRoute}>{this.props.contextName}</MarketLink>

          <HiddenOnPlatform mobile mobileBig tabletPortrait>
            <Divider paddingSize={PADDING_SMALL} />
          </HiddenOnPlatform>

          <Name>{name}</Name>

          <Divider paddingSize={PADDING_SMALL} />

          <Intro>{intro}</Intro>

          <Divider paddingSize={PADDING_SMALL} />

          <Summary
            experience={this.props.experience}
            showGroupSize
            onDiscountInfoClick={this.handleDiscountInfoClick}
          />
          <Link to={this.bookingUrl}>
            <Button label={intl.formatMessage(messages.bookingButton)} />
          </Link>
        </CoverContentWrapper>

        <Divider paddingSize={PADDING_BIG} />

        <FormattedMessage {...messages.descriptionTitle}>
          {descriptionTitle => (
            <Section title={descriptionTitle}>
              <Description>{description}</Description>
            </Section>
          )}
        </FormattedMessage>

        {this.renderWeProvideSection()}
      </>
    );
  });

  render() {
    return (
      <ThemeProvider theme={{ placeholder: this.isLoadingExperience }}>
        <Container>
          <Content>
            <Half>
              <GalleryWrapper>
                <Gallery placeholder={this.isLoadingExperience} items={this.galleryItems} />
              </GalleryWrapper>
            </Half>

            <Details>{this.renderContent(this.props.experience)}</Details>
          </Content>
        </Container>
      </ThemeProvider>
    );
  }
}
